import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux";
import { getSubscriptionsWithFiltersAction } from "../../../store/slices/subscriptions/subscriptionsSliceActions";
import { API_STATUS } from "../../../constants/apiStatus";
import { colors } from "../../../constants/colors";
import {
  Calendar,
  CreditCard,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Smartphone,
  Shield,
  RotateCcw,
  Zap,
  Tag,
  Globe,
} from "lucide-react";

const SubscriptionsSection = ({ uid }) => {
  const dispatch = useAppDispatch();
  const { subscriptionsData, apiStatus } = useAppSelector(
    (state) => state.subscriptions
  );

  // Extract subscriptions from the data structure
  const subscriptions = subscriptionsData?.subscriptions || [];

  const isLoading =
    apiStatus.getSubscriptionsWithFilters === API_STATUS.STARTED;

  // Load subscriptions when component mounts
  useEffect(() => {
    if (uid) {
      dispatch(getSubscriptionsWithFiltersAction({ userId: uid }));
    }
  }, [dispatch, uid]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get package type display
  const getPackageTypeDisplay = (packageType, subscriptionPeriod) => {
    const packageTypes = {
      MONTHLY: { text: "Monthly", color: colors.info },
      ANNUAL: { text: "Annual", color: colors.success },
      YEARLY: { text: "Yearly", color: colors.success },
      monthly: { text: "Monthly", color: colors.info },
      yearly: { text: "Yearly", color: colors.success },
      annual: { text: "Annual", color: colors.success },
    };

    // If packageType is not available, try to infer from subscriptionPeriod
    if (!packageType && subscriptionPeriod) {
      if (subscriptionPeriod === "P1M") {
        return { text: "Monthly", color: colors.info };
      } else if (subscriptionPeriod === "P1Y") {
        return { text: "Yearly", color: colors.success };
      }
    }

    return (
      packageTypes[packageType] || {
        text: packageType || "Unknown",
        color: colors.mediumGray,
      }
    );
  };

  // Get platform display
  const getPlatformDisplay = (platform, store) => {
    const platforms = {
      ios: { text: "iOS", icon: Smartphone, color: colors.info },
      android: { text: "Android", icon: Smartphone, color: colors.success },
      web: { text: "Web", icon: Globe, color: colors.primary },
      admin: { text: "Admin", icon: Shield, color: colors.darkGreen },
    };

    const storeText =
      store === "APP_STORE" || store === "app_store"
        ? "App Store"
        : store === "PLAY_STORE" || store === "play_store"
        ? "Play Store"
        : store === "ADMIN_GRANT" || store === "admin_panel"
        ? "Admin Grant"
        : store || "Unknown";

    const platformData = platforms[platform?.toLowerCase()] || {
      text: platform || "Unknown",
      icon: Smartphone,
      color: colors.mediumGray,
    };

    // Special handling for admin grants
    if (store === "ADMIN_GRANT" || store === "admin_panel") {
      return {
        ...platforms.admin,
        fullText: `Admin (${storeText})`,
      };
    }

    return {
      ...platformData,
      fullText:
        storeText && storeText !== "Unknown"
          ? `${platformData.text} (${storeText})`
          : platformData.text,
    };
  };

  // Get status badge
  const getStatusBadge = (subscription) => {
    // Determine status based on multiple fields
    let status = "pending";
    let willRenewText = "";

    if (subscription.isActive) {
      if (subscription.currentStatus === "canceled") {
        status = "canceled";
        willRenewText = subscription.willRenew ? " (Will Renew)" : "";
      } else {
        status = "active";
      }
    } else {
      status = "expired";
    }

    const statusConfig = {
      active: {
        color: colors.success,
        bg: `${colors.success}15`,
        icon: CheckCircle,
        text: "Active",
      },
      expired: {
        color: colors.warning,
        bg: `${colors.warning}15`,
        icon: Clock,
        text: "Expired",
      },
      canceled: {
        color: colors.error,
        bg: `${colors.error}15`,
        icon: XCircle,
        text: "Canceled",
      },
      pending: {
        color: colors.info,
        bg: `${colors.info}15`,
        icon: AlertCircle,
        text: "Pending",
      },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.5rem",
          padding: "0.25rem 0.75rem",
          borderRadius: "9999px",
          backgroundColor: config.bg,
          color: config.color,
          fontSize: "0.875rem",
          fontWeight: "500",
        }}
      >
        <Icon size={14} />
        {config.text}
        {willRenewText}
      </div>
    );
  };

  // Styles
  const sectionStyle = {
    backgroundColor: colors.white,
    borderRadius: "12px",
    padding: "1.5rem",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    border: `1px solid ${colors.lightGray}`,
  };

  const headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "1.5rem",
    flexWrap: "wrap",
    gap: "1rem",
  };

  const titleStyle = {
    fontSize: "1.25rem",
    fontWeight: "600",
    color: colors.darkGray,
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  };

  const cardGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))",
    gap: "1.5rem",
    marginBottom: "1.5rem",
  };

  const cardStyle = {
    border: `1px solid ${colors.lightGray}`,
    borderRadius: "8px",
    padding: "1rem",
    backgroundColor: colors.white,
    transition: "all 0.3s ease",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    height: "fit-content",
  };

  if (isLoading) {
    return (
      <div style={sectionStyle}>
        <div style={headerStyle}>
          <h3 style={titleStyle}>
            <CreditCard size={20} />
            Subscriptions
          </h3>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
            color: colors.mediumGray,
          }}
        >
          Loading subscriptions...
        </div>
      </div>
    );
  }

  return (
    <div style={sectionStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h3 style={titleStyle}>
          <CreditCard size={20} />
          Subscriptions ({subscriptions.length})
        </h3>
      </div>

      {/* Subscriptions Grid */}
      {subscriptions && subscriptions.length > 0 ? (
        <div style={cardGridStyle}>
          {subscriptions.map((subscription) => {
            const packageTypeInfo = getPackageTypeDisplay(
              subscription.packageType,
              subscription.subscriptionPeriod
            );
            const platformInfo = getPlatformDisplay(
              subscription.platform,
              subscription.store
            );

            const isAdminGranted =
              subscription.store === "ADMIN_GRANT" ||
              subscription.store === "admin_panel";

            return (
              <div
                key={subscription.subscriptionId || subscription.id}
                style={{
                  ...cardStyle,
                  background: isAdminGranted
                    ? "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)"
                    : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                  border: `2px solid ${
                    isAdminGranted ? "#0066cc" : colors.lightGray
                  }`,
                  borderRadius: "16px",
                  padding: "1.5rem",
                  position: "relative",
                  overflow: "hidden",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow =
                    "0 8px 25px rgba(0, 0, 0, 0.1)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.borderColor = isAdminGranted
                    ? "#0052cc"
                    : colors.primary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow =
                    "0 2px 8px rgba(0, 0, 0, 0.04)";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.borderColor = isAdminGranted
                    ? "#0066cc"
                    : colors.lightGray;
                }}
              >
                {/* Top section with status and package type */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "1.25rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.5rem",
                    }}
                  >
                    {getStatusBadge(subscription)}
                    {(subscription.store === "ADMIN_GRANT" ||
                      subscription.store === "admin_panel") && (
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.3rem",
                          padding: "0.25rem 0.5rem",
                          backgroundColor: "#e7f3ff",
                          color: "#0066cc",
                          borderRadius: "12px",
                          fontSize: "0.65rem",
                          fontWeight: "600",
                          border: "1px solid #b3d9ff",
                          maxWidth: "fit-content",
                        }}
                      >
                        <Shield size={10} />
                        Admin Granted
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.375rem 0.875rem",
                      borderRadius: "12px",
                      backgroundColor: `${packageTypeInfo.color}10`,
                      color: packageTypeInfo.color,
                      fontSize: "0.75rem",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    <Tag size={12} />
                    {packageTypeInfo.text}
                  </div>
                </div>

                {/* Price section */}
                <div
                  style={{
                    textAlign: "center",
                    marginBottom: "1.5rem",
                    padding: "1rem",
                    backgroundColor: colors.extraLightGray,
                    borderRadius: "12px",
                    border: `1px solid ${colors.lightGray}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: "2rem",
                      fontWeight: "700",
                      color: colors.darkGray,
                      marginBottom: "0.25rem",
                    }}
                  >
                    {subscription.priceString ||
                      (subscription.price !== undefined &&
                      subscription.price !== null
                        ? `$${subscription.price}`
                        : subscription.store === "ADMIN_GRANT" ||
                          subscription.store === "admin_panel"
                        ? "Admin Grant"
                        : "$undefined")}
                  </div>
                  <div
                    style={{
                      fontSize: "0.875rem",
                      color: colors.mediumGray,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <DollarSign size={14} />
                    {subscription.currency || "USD"} •{" "}
                    {subscription.packageType?.toLowerCase() ||
                      (subscription.subscriptionPeriod === "P1M"
                        ? "monthly"
                        : subscription.subscriptionPeriod === "P1Y"
                        ? "yearly"
                        : "subscription")}
                  </div>
                </div>

                {/* Subscription details */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "1rem",
                    marginBottom: "1.25rem",
                  }}
                >
                  <div
                    style={{
                      padding: "0.75rem",
                      backgroundColor: colors.white,
                      borderRadius: "8px",
                      border: `1px solid ${colors.lightGray}`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        color: colors.mediumGray,
                        marginBottom: "0.5rem",
                        fontSize: "0.75rem",
                        fontWeight: "500",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      <Calendar size={12} />
                      Purchase Date
                    </div>
                    <div
                      style={{
                        color: colors.darkGray,
                        fontWeight: "600",
                        fontSize: "0.875rem",
                      }}
                    >
                      {formatDate(subscription.purchaseDate)}
                    </div>
                  </div>

                  <div
                    style={{
                      padding: "0.75rem",
                      backgroundColor: colors.white,
                      borderRadius: "8px",
                      border: `1px solid ${colors.lightGray}`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        color: colors.mediumGray,
                        marginBottom: "0.5rem",
                        fontSize: "0.75rem",
                        fontWeight: "500",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      <Clock size={12} />
                      Expires
                    </div>
                    <div
                      style={{
                        color: colors.darkGray,
                        fontWeight: "600",
                        fontSize: "0.875rem",
                      }}
                    >
                      {formatDate(subscription.expirationDate)}
                    </div>
                  </div>

                  <div
                    style={{
                      padding: "0.75rem",
                      backgroundColor: colors.white,
                      borderRadius: "8px",
                      border: `1px solid ${colors.lightGray}`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        color: colors.mediumGray,
                        marginBottom: "0.5rem",
                        fontSize: "0.75rem",
                        fontWeight: "500",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      <platformInfo.icon size={12} />
                      Platform
                    </div>
                    <div
                      style={{
                        color: colors.darkGray,
                        fontWeight: "600",
                        fontSize: "0.875rem",
                      }}
                    >
                      {platformInfo.fullText}
                    </div>
                  </div>

                  <div
                    style={{
                      padding: "0.75rem",
                      backgroundColor: colors.white,
                      borderRadius: "8px",
                      border: `1px solid ${colors.lightGray}`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        color: colors.mediumGray,
                        marginBottom: "0.5rem",
                        fontSize: "0.75rem",
                        fontWeight: "500",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      <RotateCcw size={12} />
                      Auto Renew
                    </div>
                    <div
                      style={{
                        color: subscription.willRenew
                          ? colors.success
                          : colors.error,
                        fontWeight: "600",
                        fontSize: "0.875rem",
                      }}
                    >
                      {subscription.willRenew ? "Yes" : "No"}
                    </div>
                  </div>
                </div>

                {/* Additional info badges */}
                <div
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    flexWrap: "wrap",
                    marginBottom: "1rem",
                  }}
                >
                  {subscription.isTrialPeriod && (
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.375rem",
                        padding: "0.25rem 0.625rem",
                        borderRadius: "8px",
                        backgroundColor: `${colors.warning}15`,
                        color: colors.warning,
                        fontSize: "0.75rem",
                        fontWeight: "500",
                      }}
                    >
                      <Zap size={10} />
                      Trial Period
                    </div>
                  )}
                  {subscription.environment === "production" ? (
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.375rem",
                        padding: "0.25rem 0.625rem",
                        borderRadius: "8px",
                        backgroundColor: `${colors.success}15`,
                        color: colors.success,
                        fontSize: "0.75rem",
                        fontWeight: "500",
                      }}
                    >
                      <Shield size={10} />
                      Production
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.375rem",
                        padding: "0.25rem 0.625rem",
                        borderRadius: "8px",
                        backgroundColor: `${colors.warning}15`,
                        color: colors.warning,
                        fontSize: "0.75rem",
                        fontWeight: "500",
                      }}
                    >
                      <AlertCircle size={10} />
                      Sandbox
                    </div>
                  )}
                </div>

                {/* Product ID at bottom */}
                <div
                  style={{
                    padding: "0.75rem",
                    backgroundColor: colors.extraLightGray,
                    borderRadius: "8px",
                    border: `1px solid ${colors.lightGray}`,
                    marginTop: "auto",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: colors.mediumGray,
                      marginBottom: "0.25rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      fontWeight: "500",
                    }}
                  >
                    Product ID
                  </div>
                  <div
                    style={{
                      fontSize: "0.875rem",
                      color: colors.darkGray,
                      fontWeight: "500",
                      fontFamily: "monospace",
                      wordBreak: "break-all",
                    }}
                  >
                    {subscription.productId}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "3rem 1rem",
            color: colors.mediumGray,
          }}
        >
          <CreditCard
            size={48}
            style={{ marginBottom: "1rem", opacity: 0.5 }}
          />
          <h4
            style={{
              fontSize: "1.125rem",
              fontWeight: "500",
              marginBottom: "0.5rem",
              color: colors.darkGray,
            }}
          >
            No subscriptions found
          </h4>
          <p style={{ fontSize: "0.875rem", margin: 0 }}>
            This user doesn't have any subscriptions yet.
          </p>
        </div>
      )}
    </div>
  );
};

export default SubscriptionsSection;
